import React, { FC, useState, useRef, useCallback, useEffect } from 'react';
import { getYear, isBefore, addYears } from 'date-fns';
import { useHistory } from 'react-router-dom';

import { useAutoApproval, CourseListDTO } from 'hooks/useAutoApproval';

import DropdownSelector from 'components/DropdownSelector';
import DatePicker from 'components/DatePicker';
import CheckboxModal from 'components/CheckboxModal';
import AlertModal from 'components/AlertModal';
import Helper from 'components/helperIcon';
import FormSecondSection from '../FormSecondSection';

import {
  FormFirstSectionWrapper,
  ChooseNameAndSemesterWrapper,
  InputWrapper,
  SemesterSelector,
  ChooseCoursesAndDeadLine,
  InputCheckboxContainer,
  CheckboxOption,
} from './styles';

interface AvailableSemesterInterface {
  value: string;
  label: string;
}

interface DatePickerInterface {
  fromDate: Date;
  toDate: Date;
}

interface FormScreenProps {
  isEditScreen: boolean;
}

const FormScreen: FC<FormScreenProps> = ({ isEditScreen }) => {
  const history = useHistory();
  const {
    coursesList,
    coursesTableList,
    createAutoApproval,
    currentFormData,
    createAutoApprovalRelationship,
    getCurrentAutoApproval,
    getAllCoursesList,
    bolsasMaisAcesso,
    editAutoApprovalData,
    setCurrentFormData,
  } = useAutoApproval();

  const automationName = useRef<HTMLInputElement>(null);
  const [semesterOption, setSemesterOption] = useState<string>('');
  const [targetAudience, setTargetAudience] = useState<string[]>([]);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [datePickerValue, setDatePickerValue] = useState<DatePickerInterface>(
    {} as DatePickerInterface,
  );
  const [bolsaID, setBolsaID] = useState<number>(currentFormData?.bolsa || 0);
  const [semestersAvailables, setSemestersAvailables] = useState<
    AvailableSemesterInterface[]
  >([]);

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = getYear(currentDate);
    const middleYearDate = new Date(currentYear, 5, 1);

    if (isBefore(currentDate, middleYearDate)) {
      const firstSemester = `${getYear(currentDate)}/1`;
      const secondSemester = `${getYear(currentDate)}/2`;

      setSemestersAvailables([
        {
          label: firstSemester,
          value: firstSemester.replace('/', ''),
        },
        {
          label: secondSemester,
          value: secondSemester.replace('/', ''),
        },
      ]);
    } else {
      const firstSemester = `${getYear(currentDate)}/2`;
      const secondSemester = `${getYear(addYears(currentDate, 1))}/1`;

      setSemestersAvailables([
        {
          label: firstSemester,
          value: firstSemester.replace('/', ''),
        },
        {
          label: secondSemester,
          value: secondSemester.replace('/', ''),
        },
      ]);
    }
  }, []);

  useEffect(() => {
    if (bolsaID) {
      setCurrentFormData((currState) => ({ ...currState, bolsa: bolsaID }));
    }
  }, [bolsaID]);

  useEffect(() => {
    if (isEditScreen && !currentFormData.aprovacaoId) {
      history.replace('/aprovacao-automatica');
    } else if (currentFormData.aprovacaoId) {
      getCurrentAutoApproval(currentFormData.aprovacaoId);
    }
  }, []);

  useEffect(() => {
    if (automationName.current && currentFormData.aprovacaoNome) {
      automationName.current.value = currentFormData.aprovacaoNome;
    }

    if (currentFormData.tipo) {
      const initialAutoApprovalType = currentFormData.tipo?.split('');
      setTargetAudience(initialAutoApprovalType);
    }

    if (coursesList.length === 0) {
      if (currentFormData.aprovacaoId && currentFormData.bolsa) {
        getAllCoursesList({
          aprovacaoID: currentFormData.aprovacaoId,
          bolsaID: currentFormData.bolsa,
        });
      }
    }
  }, [automationName, currentFormData]);

  useEffect(() => {
    if (currentFormData.aprovacaoId && currentFormData.bolsa) {
      getAllCoursesList({
        aprovacaoID: currentFormData.aprovacaoId,
        bolsaID: currentFormData.bolsa,
      });
    }
  }, [datePickerValue]);

  const handleTargetAudience = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, value: string): void => {
      const { checked } = event.target;

      if (checked) {
        setTargetAudience((currArray) => [...currArray, value]);
      } else {
        setTargetAudience((currArray): string[] => {
          const newArray = currArray?.filter((currItem) => currItem !== value);

          return newArray;
        });
      }
    },
    [currentFormData],
  );

  const handleSaveCalendar = useCallback(async (): Promise<void> => {
    if (
      automationName.current?.value &&
      semesterOption &&
      targetAudience.length > 0 &&
      datePickerValue.fromDate &&
      datePickerValue.toDate &&
      bolsaID !== 0
    ) {
      const hasVeteranos = targetAudience.some(
        (targetItem) => targetItem === 'V' || targetItem === 'Veteranos',
      );

      const hasCalouros = targetAudience.some(
        (targetItem) => targetItem === 'C' || targetItem === 'Calouros',
      );

      const tipo = `${hasCalouros ? 'C' : ''}${hasVeteranos ? 'V' : ''}`;

      if (!isEditScreen) {
        const createResponse = await createAutoApproval({
          bolsa: bolsaID,
          nome: automationName.current.value,
          periodo: semesterOption,
          prazoFinal: datePickerValue.toDate,
          prazoInicial: datePickerValue.fromDate,
          status: 'R',
          tipo,
        });

        await getAllCoursesList({
          bolsaID,
          aprovacaoID: createResponse.id,
        });
      } else {
        editAutoApprovalData({
          id: currentFormData.aprovacaoId || 0,
          nome: automationName.current.value,
          periodo: semesterOption,
          prazoFinal: datePickerValue.toDate,
          prazoInicial: datePickerValue.fromDate,
          status: 'R',
          tipo,
        });
      }
    }
  }, [
    automationName,
    semesterOption,
    targetAudience,
    datePickerValue,
    bolsaID,
  ]);

  const handleSaveAndExit = useCallback(() => {
    const hasVeteranos = targetAudience.some(
      (targetItem) => targetItem === 'V' || targetItem === 'Veteranos',
    );

    const hasCalouros = targetAudience.some(
      (targetItem) => targetItem === 'C' || targetItem === 'Calouros',
    );

    const tipo = `${hasCalouros ? 'C' : ''}${hasVeteranos ? 'V' : ''}`;

    if (automationName.current) {
      editAutoApprovalData({
        id: currentFormData.aprovacaoId || 0,
        nome: automationName.current.value,
        periodo: semesterOption,
        prazoFinal: datePickerValue.toDate,
        prazoInicial: datePickerValue.fromDate,
        status: 'R',
        tipo,
      });
    }
  }, [automationName, semesterOption, targetAudience, datePickerValue]);

  const handleSelectedCourses = useCallback(
    async (courses: CourseListDTO[]) => {
      try {
        const coursesRelations = courses.map((item) => {
          return {
            aprovacaoAutomaticaId: currentFormData.aprovacaoId,
            cursoId: item.id,
            mensalidade: item.mensalidade
          };
        });

        await createAutoApprovalRelationship(coursesRelations);

        await getCurrentAutoApproval(currentFormData.aprovacaoId as number);
      } catch (err) {
        alert('Algo deu errado, tente novamente');
      }
    },
    [currentFormData.aprovacaoId],
  );

  const handleValidation = (field: string) => {
    if (field === 'coursesList') {
      if (!automationName.current?.value)
        setMissingFields((currFields) => [...currFields, 'Nome da aprovação']);

      if (!semesterOption)
        setMissingFields((currFields) => [...currFields, 'Ano/Semestre']);

      if (bolsaID === 0)
        setMissingFields((currFields) => [...currFields, 'Convênio']);

      if (targetAudience.length === 0)
        setMissingFields((currFields) => [...currFields, 'Público alvo']);

      if (!datePickerValue.fromDate || !datePickerValue.toDate)
        setMissingFields((currFields) => [
          ...currFields,
          'Prazo das inscrições',
        ]);

      return;
    }

    if (field === 'datePicker') {
      if (!automationName.current?.value)
        setMissingFields((currFields) => [...currFields, 'Nome da aprovação']);

      if (!semesterOption)
        setMissingFields((currFields) => [...currFields, 'Ano/Semestre']);

      if (bolsaID === 0)
        setMissingFields((currFields) => [...currFields, 'Convênio']);

      if (targetAudience.length === 0)
        setMissingFields((currFields) => [...currFields, 'Público alvo']);
    }
  };

  console.log('hahaa', semestersAvailables);

  return (
    <>
      {missingFields.length > 0 && (
        <AlertModal
          fields={missingFields}
          handleOnClose={() => setMissingFields([])}
        />
      )}
      <FormFirstSectionWrapper
        increaseMinHeight={coursesTableList?.length <= 0}
      >
        <ChooseNameAndSemesterWrapper>
          <InputWrapper isTypeText flexSize={2.5} mr={50}>
            <label htmlFor="automationName">
              Escolha o nome da automação
              <Helper message="Dê um nome para sua automação. Crie nomes únicos e de fácil identificação. O nome aparecerá na lista de automações." />
            </label>
            <input type="text" id="automationName" ref={automationName} />
          </InputWrapper>

          <SemesterSelector>
            <label>
              Ano/Semestre
              <Helper message="Selecione o Ano/Semestre que a automação será válida. Essa seleção influenciará na lista de cursos selecionados posteriormente." />
            </label>
            <DropdownSelector
              placeholder="Selecione"
              data={semestersAvailables}
              initialValue={currentFormData.periodo}
              setFatherState={setSemesterOption}
            />
          </SemesterSelector>

          <SemesterSelector>
            <label>
              Convênio
              <Helper message="Escolha um dos convênios na listagem para ligar a automação." />
            </label>
            <DropdownSelector
              bolsaInitialId={currentFormData.bolsa}
              placeholder="Selecione"
              data={bolsasMaisAcesso.map((item) => ({
                label: item.sigla,
                value: item.id,
              }))}
              fillWidth
              setFatherState={setBolsaID}
            />
          </SemesterSelector>
        </ChooseNameAndSemesterWrapper>

        <ChooseCoursesAndDeadLine>
          <InputWrapper>
            <label htmlFor="automationName">
              Escolha o público alvo
              <Helper message="A automação pode ser ligada para veteranos e/ou calouros. Selecione para qual público ela funcionará." />
            </label>
            <InputCheckboxContainer>
              <CheckboxOption>
                <input
                  type="checkbox"
                  id="veteranOption"
                  onChange={(e) => handleTargetAudience(e, 'V')}
                  checked={targetAudience.some(
                    (targetItem) =>
                      targetItem === 'V' || targetItem === 'Veteranos',
                  )}
                />
                <label htmlFor="veteranOption">Veteranos</label>
              </CheckboxOption>

              <CheckboxOption>
                <input
                  type="checkbox"
                  id="noviceOption"
                  onChange={(e) => handleTargetAudience(e, 'C')}
                  checked={targetAudience.some(
                    (targetItem) =>
                      targetItem === 'C' || targetItem === 'Calouros',
                  )}
                />
                <label htmlFor="noviceOption">Calouros</label>
              </CheckboxOption>
            </InputCheckboxContainer>
          </InputWrapper>

          <InputWrapper onClick={() => handleValidation('datePicker')}>
            <label htmlFor="automationName">
              Escolha o prazo para inscrições
              <Helper message="A automação ficará ativa no período selecionado aqui. Estudantes que concluírem uma solicitação entre o período, serão aprovados automaticamente." />
            </label>
            <DatePicker
              initialValue={{
                from: currentFormData.prazoInicial,
                to: currentFormData.prazoFinal,
              }}
              isVisible={missingFields.length === 0}
              setDatePickerValue={setDatePickerValue}
              handleSaveCalendar={handleSaveCalendar}
            />
          </InputWrapper>

          <InputWrapper
            flexSize={1.6}
            ml={45}
            onClick={() => handleValidation('coursesList')}
          >
            <label htmlFor="automationName">
              Escolha os cursos
              <Helper message="Os cursos listados aqui são os cadastrados no sistema da Fundacred, para o ano/semestre selecionado anteriormente. Selecione 1 ou mais cursos." />
            </label>
            <CheckboxModal
              initialValue={coursesTableList}
              isVisible={missingFields.length === 0}
              data={coursesList}
              handleSelectedCourses={handleSelectedCourses}
            />
          </InputWrapper>
        </ChooseCoursesAndDeadLine>
      </FormFirstSectionWrapper>
      {coursesTableList && coursesTableList.length > 0 && (
        <FormSecondSection
          selectedCourses={coursesTableList}
          handleSaveAndExit={handleSaveAndExit}
        />
      )}
    </>
  );
};

export default FormScreen;
